import { Tab } from '@headlessui/react'
import router from 'next/router'
import React, { useState } from 'react'
import Lottie from 'react-lottie'
import { VideoJsPlayerOptions } from 'video.js'

import { TabButton, VideoPlayer } from '@/components/common/DataDisplay'
import {
  GradationButton,
  IconMute,
  IconPlayArrow,
  IconShare,
  IconUnMute,
} from '@/components/common/General'
import { Blur } from '@/components/common/Util'
import { WorkTabPanel, WorkTitle } from '@/components/domain/works'
import { useShare } from '@/hooks/useShare'
import { useVideoPlayer } from '@/hooks/useVideoPlayer'

import { pagesPath } from '@/lib/$path'

import * as loadingAnimation from '../../../../public/loading-animation.json'

import { useWorkDetails } from './useWorkDetails'

enum EnumPanelType {
  main,
  making,
}

const lottieDefaultOptions = {
  loop: true,
  autoplay: true,
  animationData: loadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

export const WorkDetails = () => {
  const {
    uuid,
    workTitleProps,
    mainTabPanelProps,
    makingTabPanelProps,
    mainStoryProjectSeason,
    makingStoryProjectSeason,
    workDetail,
    mainStoryProject,
    makingStoryProject,
    isWebViewMode,
    projectName,
  } = useWorkDetails()
  const [panelType, setPanelType] = useState(
    projectName === '本編' ? EnumPanelType.main : EnumPanelType.making
  )
  const digestUrl =
    panelType === EnumPanelType.main
      ? mainStoryProjectSeason?.digestVideoUrl?.replace('-playlist', '')
      : makingStoryProjectSeason?.digestVideoUrl?.replace('-playlist', '')

  const { onClickShareSNS } = useShare()

  const handleClickShareSNS = React.useCallback(() => {
    onClickShareSNS({
      title: workDetail.name,
      text: `✨――――――――✨\nBUMPで無料配信中\n✨――――――――✨\n\n${workDetail?.name}\n\n#BUMP #${workDetail?.name}`,
      url: `https://www.bump.studio/works/${uuid}`,
    })
  }, [onClickShareSNS, uuid, workDetail.name])
  const options = React.useMemo((): VideoJsPlayerOptions => {
    return {
      autoplay: true,
      muted: true,
      controls: false,
      bigPlayButton: false,
      fluid: true,
      controlBar: false,
      sources: [
        {
          src: digestUrl || '',
          type: 'application/x-mpegURL',
        },
      ],
      html5: {
        vhs: {
          overrideNative: true,
        },
      },
    }
  }, [digestUrl])

  const { videoRef, toggleMute, isMute } = useVideoPlayer({
    options,
    autoPlay: 'muted',
    isReady: !!mainStoryProjectSeason?.digestVideoUrl,
  })

  if (!workDetail) {
    return (
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Lottie options={lottieDefaultOptions} width={100} height={100} />
      </div>
    )
  }

  if (!mainStoryProject || !mainStoryProjectSeason) {
    return null
  }

  return (
    <div>
      <div className="relative">
        <div
          className={`${
            mainStoryProjectSeason.isPortrait ? 'aspect-[5/8]' : 'aspect-[16/9]'
          }`}
        >
          <VideoPlayer ref={videoRef} />
        </div>

        {mainStoryProjectSeason.isPortrait && (
          <Blur h="h-[160px]" direction="top" className="-bottom-[68px]" />
        )}

        {!isWebViewMode && (
          <div className="flex absolute top-[24px] right-0 items-center">
            <button
              aria-label={isMute ? 'ミュートを解除する' : 'ミュートにする'}
              type="button"
              onClick={toggleMute}
              className="mr-[20px] text-2xl"
            >
              {isMute ? <IconMute /> : <IconUnMute />}
            </button>
            <button
              aria-label="シェアする"
              type="button"
              className="mr-[20px] text-2xl"
              onClick={handleClickShareSNS}
            >
              <IconShare />
            </button>
          </div>
        )}

        <div
          className={`flex flex-col justify-center px-16 w-full ${
            mainStoryProjectSeason.isPortrait
              ? 'absolute bottom-16 z-10'
              : 'static mt-16'
          }`}
        >
          <WorkTitle
            workDetailName={workTitleProps.workDetailName}
            projectName={
              panelType === EnumPanelType.main
                ? mainStoryProject.name ?? ''
                : makingStoryProject?.name ?? ''
            }
            projectSeason={
              panelType === EnumPanelType.main
                ? mainStoryProjectSeason
                : makingStoryProjectSeason
            }
          />
          <GradationButton
            size="lg"
            leftIcon={<IconPlayArrow />}
            className="mt-16"
            onClick={() => {
              const episodeId =
                panelType === EnumPanelType.main
                  ? mainStoryProjectSeason.episodes?.find(() => true)?.id
                  : makingStoryProjectSeason?.episodes?.find(() => true)?.id
              router.push(pagesPath.episodes._uuid(episodeId ?? '').$url())
            }}
          >
            {panelType == EnumPanelType.main ? '本編' : '制作裏側'}無料視聴
          </GradationButton>
        </div>
      </div>

      <div className="pt-[24px]">
        <Tab.Group defaultIndex={panelType}>
          <Tab.List className="relative z-20 px-16 border-b border-ui-2">
            <TabButton>
              <button
                onClick={() => {
                  setPanelType(EnumPanelType.main)
                }}
              >
                本編
              </button>
            </TabButton>
            <TabButton>
              <button
                onClick={() => {
                  setPanelType(EnumPanelType.making)
                }}
              >
                制作裏側
              </button>
            </TabButton>
          </Tab.List>
          <Tab.Panels className="px-16">
            <WorkTabPanel {...mainTabPanelProps} />
            <WorkTabPanel {...makingTabPanelProps} />
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  )
}
